import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import BackToAll from "@/components/feature/BackToAll";
import Seo, { getAbsoluteUrl } from "@/components/feature/Seo";
import { buildBreadcrumbSchema, type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import { brand } from "@/mocks/siteData";
import { usePublicStories } from "@/hooks/usePublicStories";
import StoryArticleHero from "./components/StoryArticleHero";
import StoryArticleBody from "./components/StoryArticleBody";
import StoryArticleGallery from "./components/StoryArticleGallery";
import RelatedStories from "./components/RelatedStories";
import StoryArticleCta from "./components/StoryArticleCta";

const toIsoDate = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().split("T")[0];
};

export default function StoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { stories, loading, error, reload } = usePublicStories();

  const story = slug ? stories.find((item) => item.slug === slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        <Navbar />
        <main className="w-full pt-32 pb-24 min-h-[70vh] flex flex-col items-center justify-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
          <p className="text-sm text-foreground-600">Loading story…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        <Seo
          title={`Unable to load this story | ${brand.name}`}
          description="This story couldn't be loaded right now. Browse the Rythugaru journal for farmer stories and field diaries from Konaseema."
          path={`/stories/${slug ?? ""}`}
          noindex
        />
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <Reveal className="text-center max-w-lg bg-accent-100 border border-accent-300 rounded-lg px-6 py-8">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-200 text-accent-900">
              <i className="ri-error-warning-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mt-6">
              Something went wrong
            </h1>
            <p className="mt-3 text-foreground-700 leading-relaxed">{error}</p>
            <button
              type="button"
              onClick={reload}
              className="mt-7 inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i> Try again
            </button>
          </Reveal>
        </main>
        <Footer />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        {/* Unknown slug: noindex, so a dead story URL is never indexed as a copy of the previous page. */}
        <Seo
          title={`Story not found | ${brand.name}`}
          description="This story may have moved or is no longer available. Browse the Rythugaru journal for farmer stories and field diaries from Konaseema."
          path={`/stories/${slug ?? ""}`}
          noindex
        />
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <Reveal className="text-center max-w-lg">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-book-open-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-6">
              Story not found
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              This story may have moved or is no longer available. Browse the journal to find other
              stories from the soil.
            </p>
            <Link
              to="/stories"
              className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to all stories
              <i className="ri-arrow-right-line"></i>
            </Link>
          </Reveal>
        </main>
        <Footer />
      </div>
    );
  }

  const storyUrl = getAbsoluteUrl(`/stories/${story.slug}`);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", to: "/" },
    { label: "Stories From the Soil", to: "/stories" },
    { label: story.title },
  ];

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`${story.title} | ${brand.name}`}
        description={story.excerpt}
        tokens={{ title: story.title, description: story.excerpt }}
        path={`/stories/${story.slug}`}
        ogType="article"
        keywords={`${story.category}, ${story.village} farming story, Konaseema farm stories, agri tourism, rural India stories, ${brand.name}`}
        image={story.cover}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: story.title,
            description: story.excerpt,
            image: story.cover,
            url: storyUrl,
            datePublished: toIsoDate(story.isoDate || story.date),
            dateModified: toIsoDate(story.isoDate || story.date),
            articleSection: story.category,
            author: { "@type": "Person", name: story.author },
            publisher: { "@type": "Organization", name: brand.fullName },
            mainEntityOfPage: { "@type": "WebPage", "@id": storyUrl },
          },
          buildBreadcrumbSchema(breadcrumbs, storyUrl),
        ]}
      />
      <Navbar />
      <main>
        <StoryArticleHero story={story} breadcrumbs={breadcrumbs} />
        <StoryArticleBody story={story} />
        <StoryArticleGallery story={story} />
        <RelatedStories current={story} all={stories} />
        <StoryArticleCta author={story.author} />
        <BackToAll to="/stories" label="Back to all stories" icon="ri-book-open-line" />
      </main>
      <Footer />
    </div>
  );
}